import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getBoardDetail, type BoardDetail } from '../../utils/board';
import {
  createBoardCategory,
  deleteBoardCategory,
  getBoardCategoryList,
  sortBoardCategories,
  updateBoardCategory,
  type BoardCategory,
} from '../../utils/boardCategory';
import { generateAfter, generateBetween } from '../../utils/fractionalIndex';
import { ArrowLeft, Edit, FolderTree, GripVertical, Plus, Trash2, X } from 'lucide-react';

const CODE_PATTERN = /^[a-zA-Z0-9_-]+$/;
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
];

interface CategoryFormState {
  code: string;
  name: string;
  status: string;
}

const EMPTY_FORM: CategoryFormState = {
  code: '',
  name: '',
  status: 'ACTIVE',
};

function generateCategoryCode(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function sortOrderAtIndex(items: BoardCategory[], index: number): string {
  const prev = index > 0 ? items[index - 1].sortOrder : null;
  const next = index < items.length - 1 ? items[index + 1].sortOrder : null;
  return generateBetween(prev, next);
}

const BoardCategoryConfig: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl, boardCode: boardCodeFromUrl } = useParams<{
    projectCode: string;
    boardCode: string;
  }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('board-list');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [boardDetail, setBoardDetail] = useState<BoardDetail | null>(null);
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [reorderingCode, setReorderingCode] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<BoardCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  const loadData = useCallback(async () => {
    if (!projectCodeFromUrl || !boardCodeFromUrl) return;
    setError(null);
    setIsLoading(true);
    try {
      const [board, list] = await Promise.all([
        getBoardDetail(projectCodeFromUrl, boardCodeFromUrl),
        getBoardCategoryList(projectCodeFromUrl, boardCodeFromUrl, {
          includePostCount: true,
        }),
      ]);
      setBoardDetail(board);
      setCategories(sortBoardCategories(list));
    } catch (err) {
      setError(err instanceof Error ? err.message : '카테고리 정보를 불러오지 못했습니다.');
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectCodeFromUrl, boardCodeFromUrl]);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectDetail || !boardCodeFromUrl) return;
    loadData();
  }, [projectDetail, boardCodeFromUrl, loadData]);

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; text: string }> = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-800' },
    };
    const style = map[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
    const label = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        {label}
      </span>
    );
  };

  const handleOpenCreate = () => {
    setEditCategory(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (category: BoardCategory) => {
    setEditCategory(category);
    setFormData({
      code: category.code,
      name: category.name,
      status: category.status || 'ACTIVE',
    });
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setEditCategory(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setModalError(null);
  };

  const handleAutoGenerateCode = () => {
    setFormData((prev) => ({ ...prev, code: generateCategoryCode() }));
    if (formErrors.code) setFormErrors((prev) => ({ ...prev, code: '' }));
  };

  const validateForm = (): boolean => {
    const next: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();

    if (!editCategory) {
      if (!code) next.code = '코드를 입력해주세요.';
      else if (code.length > 20) next.code = '코드는 최대 20자입니다.';
      else if (!CODE_PATTERN.test(code)) next.code = '영문, 숫자, _, - 만 사용할 수 있습니다.';
      else if (categories.some((c) => c.code === code)) next.code = '이미 사용 중인 코드입니다.';
    }

    if (!name) next.name = '카테고리명을 입력해주세요.';
    else if (name.length > 50) next.name = '카테고리명은 최대 50자입니다.';

    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectCodeFromUrl || !boardCodeFromUrl || !validateForm()) return;

    setIsSubmitting(true);
    setModalError(null);
    try {
      if (editCategory) {
        await updateBoardCategory(projectCodeFromUrl, boardCodeFromUrl, editCategory.code, {
          code: editCategory.code,
          name: formData.name.trim(),
          status: formData.status,
          sortOrder: editCategory.sortOrder,
        });
      } else {
        const lastSort = categories.length ? categories[categories.length - 1].sortOrder : null;
        await createBoardCategory(projectCodeFromUrl, boardCodeFromUrl, {
          code: formData.code.trim(),
          name: formData.name.trim(),
          status: formData.status,
          sortOrder: generateAfter(lastSort),
        });
      }
      handleCloseModal();
      await loadData();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (category: BoardCategory) => {
    if (!projectCodeFromUrl || !boardCodeFromUrl) return;
    if (!window.confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?`)) return;

    try {
      await deleteBoardCategory(projectCodeFromUrl, boardCodeFromUrl, category.code);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : '카테고리 삭제에 실패했습니다.');
    }
  };

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = useCallback(
    (dropIndex: number) => async (e: React.DragEvent) => {
      e.preventDefault();
      const from = Number(e.dataTransfer.getData('text/plain'));
      setDragIndex(null);

      if (
        Number.isNaN(from) ||
        from === dropIndex ||
        !projectCodeFromUrl ||
        !boardCodeFromUrl ||
        reorderingCode
      ) {
        return;
      }

      const movedCategory = categories[from];
      const reordered = moveItem(categories, from, dropIndex);
      const newSortOrder = sortOrderAtIndex(reordered, dropIndex);
      const optimistic = reordered.map((c) =>
        c.code === movedCategory.code ? { ...c, sortOrder: newSortOrder } : c
      );

      setCategories(sortBoardCategories(optimistic));
      setReorderingCode(movedCategory.code);
      setError(null);

      try {
        await updateBoardCategory(projectCodeFromUrl, boardCodeFromUrl, movedCategory.code, {
          code: movedCategory.code,
          name: movedCategory.name,
          status: movedCategory.status,
          sortOrder: newSortOrder,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : '정렬 순서 변경에 실패했습니다.');
        await loadData();
      } finally {
        setReorderingCode(null);
      }
    },
    [categories, projectCodeFromUrl, boardCodeFromUrl, reorderingCode, loadData]
  );

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  const boardTitle = boardDetail?.name ?? boardCodeFromUrl ?? '';
  const editPostCount = editCategory?.postCount ?? 0;
  const codeLockedOnEdit = !!editCategory && editPostCount > 0;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="카테고리 설정"
          subtitle={
            projectCodeFromUrl && boardCodeFromUrl
              ? `${projectDetail.name} / ${boardTitle} (${boardCodeFromUrl})`
              : '게시판 카테고리를 관리합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate(`/projects/${projectCodeFromUrl}/boards`)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                게시판 목록으로
              </button>
              <button
                type="button"
                onClick={handleOpenCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                카테고리 추가
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-2 text-red-800 underline"
                >
                  닫기
                </button>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center gap-2">
                <FolderTree className="w-5 h-5 text-blue-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{boardTitle}</h2>
                  <p className="text-sm text-gray-500">
                    드래그하여 순서를 변경할 수 있습니다. (총 {categories.length}개)
                  </p>
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-10 px-2 py-3" />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          코드
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          카테고리명
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          게시물 수
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">작업</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                            등록된 카테고리가 없습니다. 상단에서 카테고리를 추가해주세요.
                          </td>
                        </tr>
                      ) : (
                        categories.map((category, index) => (
                          <tr
                            key={category.code}
                            draggable={!reorderingCode}
                            onDragStart={onDragStart(index)}
                            onDragOver={onDragOver}
                            onDrop={onDrop(index)}
                            className={`hover:bg-gray-50 ${
                              dragIndex === index ? 'opacity-60' : ''
                            } ${reorderingCode === category.code ? 'bg-blue-50/50' : ''}`}
                          >
                            <td className="px-2 py-4 cursor-grab active:cursor-grabbing text-gray-400">
                              <GripVertical className="w-5 h-5" />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {category.code}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900">{category.name}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {getStatusBadge(category.status)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                              {Number(category.postCount ?? 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-blue-600"
                                  title="수정"
                                  onClick={() => handleOpenEdit(category)}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-600"
                                  title="삭제"
                                  onClick={() => handleDelete(category)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editCategory ? '카테고리 수정' : '카테고리 추가'}
              </h2>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
              {modalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {modalError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  코드 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20);
                      setFormData((prev) => ({ ...prev, code: value }));
                      if (formErrors.code) setFormErrors((prev) => ({ ...prev, code: '' }));
                    }}
                    placeholder="영문, 숫자, _, - (최대 20자)"
                    disabled={!!editCategory}
                    className={`flex-1 min-w-0 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.code ? 'border-red-500' : 'border-gray-300'
                    } ${editCategory ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                    maxLength={20}
                  />
                  {!editCategory && (
                    <button
                      type="button"
                      onClick={handleAutoGenerateCode}
                      className="flex-shrink-0 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                    >
                      자동생성
                    </button>
                  )}
                </div>
                {codeLockedOnEdit && (
                  <p className="mt-1 text-xs text-gray-500">
                    게시물이 있는 경우 코드를 수정할 수 없습니다.
                  </p>
                )}
                {formErrors.code && <p className="mt-1 text-sm text-red-500">{formErrors.code}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카테고리명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, name: e.target.value }));
                    if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  placeholder="카테고리명 (최대 50자)"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  maxLength={50}
                />
                {formErrors.name && <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardCategoryConfig;
